/**
 * ComplementaryInfo Component
 *
 * Displays complementary course information such as reviews, ratings, enrolled students, subtitles, audio, and share link.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Conditional rendering of reviews and positive feedback
 * - Displays enrolled students, subtitles, and audio info
 * - Share link functionality with copy feedback
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing course info
 * @param {Object} props.aditionalData - Additional data for reviews and subtitles
 * @param {number} props.usersInCourse - Number of enrolled students
 * @returns {JSX.Element} The rendered complementary info section
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactStars from 'react-rating-stars-component';
import Button from '@mui/material/Button';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ClosedCaptionOutlinedIcon from '@mui/icons-material/ClosedCaptionOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import ShareIcon from '@mui/icons-material/Share';

import classes from './ComplementaryInfo.module.css';
const urlNew = window.location.host;

const ComplementaryInfo = ({
  data,
  aditionalData,
  usersInCourse
}) => {
  const { t } = useTranslation(['course']);
  const [linkCopied, setLinkCopied] = useState(false);
  const copyLinkHandler = () => {
    const copyText = document.getElementById('urlCourseCopy');
    copyText.select();
    copyText.setSelectionRange(0, 99999);
    document.execCommand('copy');
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
    }, 3000);
  };
  return (
    <div className={classes.extraInfoContainer}>
      <aside className={classes.extraInfoReviewsSection}>
        {!data.reviewsEnabled ? (
          <>
            <p className={classes.textDescriptionCourse}>
              {t('user_reviews')}
            </p>
            <aside className={classes.starsContainer}>
              <ReactStars
                edit={false}
                count={5}
                isHalf={true}
                size={24}
                activeColor="#e6a817"
                emptyStarColor="#d6d6d6"
                value={data.votes / data.noReviews}
              />
              {/* Props passed: votes and number of reviews */}
            </aside>
            {parseInt(aditionalData.positiveRatingPercent) > 50 && (
              <p className={classes.textUsersCourse}>
                {parseInt(aditionalData.positiveRatingPercent)}%{' '}
                {t('positive_feedback')} (
                {aditionalData.totalReviews})
                {/* Props passed: positive rating percent and total reviews */}
              </p>
            )}
          </>
        ) : (
          <p className={classes.textUsersCourseBlack}>.</p>
        )}
      </aside>
      <aside className={classes.extraInfoSection}>
        {usersInCourse >= 20 && (
          <p className={classes.textUsersCourse}>
            <GroupOutlinedIcon className={classes.iconStudents} />
            {t('enrolled_students')}: {usersInCourse}
            {/* Props passed: enrolled students count */}
          </p>
        )}
        <p className={classes.textUsersCourse}>
          <ClosedCaptionOutlinedIcon
            className={classes.iconStudents}
          />
          {aditionalData.ccData.map(cc => (
            <span key={cc.language}>{cc.description}</span>
            // Props passed: subtitle description
          ))}
        </p>
        <Button
          className={classes.shareBtn}
          onClick={copyLinkHandler}
        >
          <ShareIcon className={classes.iconShare} /> {t('share')}
        </Button>
        <input
          className={classes.shareInput}
          type="text"
          value={`https://${urlNew}/${data.shortNameEmisor}/c/${data.idLista}`}
          id="urlCourseCopy"
        />
        {linkCopied && (
          <p className={classes.shareText}>{t('copied_link')}</p>
        )}
      </aside>
      <aside className={classes.extraInfoSection}>
        <p className={classes.textUsersCourse}>
          <VolumeUpOutlinedIcon className={classes.iconStudents} />
          {t('audio')}: Español
          {/* Props passed: audio language */}
        </p>
      </aside>
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
ComplementaryInfo.propTypes = {
  /**
   * Data object containing course info
   * Passed as prop to render complementary information
   */
  data: PropTypes.object,
  aditionalData: PropTypes.object,
  usersInCourse: PropTypes.number
};

ComplementaryInfo.defaultProps = {
  data: {},
  aditionalData: {},
  usersInCourse: 0
};

export default ComplementaryInfo;
